import { EVOLUTIONS_A } from './evolutions-a';
import { EVOLUTIONS_B } from './evolutions-b';
import { EVOLUTIONS_C } from './evolutions-c';
import type { PoolWeapon } from './weapons-pool';
/* ============================================================
   進化（M6）
   ============================================================

   **滿級武裝 + 滿級的指定被動 → 進化型**，進化取代原本那把、不佔額外的槽。

   進化是 build 的頂點，也是 20 分鐘後半唯一的目標 ——
   沒有它，第 12 分鐘之後玩家就只是在把數字堆高（GDD §7.4）。

   ## 2026-08-05 進化**不再只有一級**（`CRAFT_MAX = 3`）

   原本的規則是「拿到就是完全體」（跟 VS 一致）。五次配平實驗之後那條規則
   被證明是後期決策真空的**結構原因**：進化取代來源武裝之後那一格再也吃不下
   任何升級，18 條質變全部拿到＝五個武裝槽全是死格＝抽卡池更早見底
   （`.claude/notes/enjoy-gap-2026-08-05.md` 的實驗 E）。

   現在：**Lv1 從寶箱拿（＝原本的完全體，數值一字未動），Lv2/Lv3 走升級選單。**
   質變有前置條件、天然只在中後期成立，所以它是唯一不與前期競爭的供給。
   每一級的施工契約（三級的語言、1.6–1.9× 帶寬、必填的 `turn`/`turnD`）
   寫在 `.claude/notes/craft-levels.md`。

   「進化」在升級介面上仍然有明確的份量 —— 它不是「再 +10%」，是換一把武器；
   Lv3 那一級要**再拿掉一條限制**，不是把數字堆高。

   ## 2026-08-02 依 VS 的邏輯改寫：**進化 = 解除限制，不是加傷害**

   逐頁查證的十組 VS 進化，沒有一組的重點是數值（`.claude/notes/vs-weapons.md`）：

     King Bible → Unholy Vespers  把「冷卻」與「持續時間」**脫鉤** → 變成永久環繞
     Runetracer → NO FUTURE       穿透次數的**上限移除**，另外加爆炸
     Axe        → Death Spiral    拋物線飛行的**隨機性移除**，變成固定環繞

   所以寫進化的第一個問題是「**這把武裝身上最煩人的限制是什麼**」，
   然後拿掉它。拿掉限制才會讓玩家在拿到的那一秒發出聲音；+40% 傷害不會。
   每個進化的註解都要寫明它拿掉的是哪一條限制。

   ## 2026-08-05 配平語言與設計語言要分開驗

   第一批六個質變全部量到「進化之後比來源還弱」（中位數 ×0.76），
   而每一條的設計意圖都是對的。真因是：

   > **「拿掉哪條限制」是設計語言，它不回答「拿掉之後每秒總輸出是多少」。**

   所以寫完一條進化的下一件事是 `pnpm crafts`，對照 04 要求的 2.5–4× 帶寬。
   不要等六條全部寫完再一起量。

   ## 2026-08-05 第二批：覆蓋率 4 → 10 條

   在這之前 10 把池子武裝裡**只有 4 把有進化路線**，於是質變全部擠在第 4–6 分鐘
   （`pnpm pacing` 第 5 節：13 次全部落在 4:08–6:36）—— 材料一用完就沒有下一個了，
   而 11–14 分那個跳升點需要「那時候還有沒拿到的質變」。

   第二批補上 `w-pulse` `w-drone` `w-aura` `w-rear` `w-ricochet` 各一條，
   外加 `w-mine` 的**第二條路線**（04 明文要求「每把武器至少 2 條進化路線，
   且體感有差異」）。`w-tether` 沒有進化 —— 它本身不造成傷害，
   量測台的「來源零輸出」斷言會擋掉，它的分岔改由融合提供（`data/unions.ts`）。

   一把武裝的兩條路線**鑰匙不同**：滿級了哪個被動決定你走哪一條，
   被動選擇因此有 build 上的意義，而不只是加成。 */

export type Evolution = PoolWeapon & { evo: { from: string, needs: string } };

/* 順序＝寶箱與圖鑑的列出順序，所以 A → B → C 是有意義的（`sim/chest.ts` 的
   合成品格從 `craftIds()` 取，而那份清單就是照這個順序走）。 */
export var EVOLUTIONS: Evolution[] = EVOLUTIONS_A.concat(EVOLUTIONS_B).concat(EVOLUTIONS_C);

export function evolutionById(id) {
  for (var i = 0; i < EVOLUTIONS.length; i++) if (EVOLUTIONS[i].id === id) return EVOLUTIONS[i];
  return null;
}

/** 這把武裝有哪些進化路線（一對多）。給說明文字與圖鑑用。 */
export function evolutionsFrom(weaponId) {
  var out: Evolution[] = [];
  for (var i = 0; i < EVOLUTIONS.length; i++) if (EVOLUTIONS[i].evo.from === weaponId) out.push(EVOLUTIONS[i]);
  return out;
}
